// The support chat, its documents and the console. A person's identity is never in these: it comes from the session.
import { z } from "zod";
import { IsoDateTime, PageQuery, Uuid, pageOf } from "./common";
import { UiLang } from "./identity";

export const SUPPORT_CONVERSATION_STATUSES = ["ai", "waiting", "human", "closed"] as const;
export const SUPPORT_MESSAGE_ROLES = ["user", "ai", "staff", "system"] as const;
export const SUPPORT_OUTCOMES = ["answered", "clarify", "escalate", "smalltalk"] as const;
export const SUPPORT_ESCALATION_REASONS = ["asked", "ai", "daily_limit", "no_documents", "kb_unavailable", "provider_failed", "stale"] as const;
export const SUPPORT_DOCUMENT_STATUSES = ["active", "draft", "retired"] as const;
export const SUPPORT_DOCUMENT_SOURCES = ["repo", "console", "learning"] as const;
export const SUPPORT_PROPOSAL_STATUSES = ["proposed", "approved", "rejected"] as const;
export const SUPPORT_VERTICALS = ["horeca", "auto", "medical"] as const;

export const SUPPORT_MAX_TEXT = 4000;
export const SUPPORT_DEFAULT_DAILY_LIMIT = 50;

const SupportText = z.string().trim().min(1).max(SUPPORT_MAX_TEXT);
const ClientId = z.string().trim().min(8).max(64);
const Page = z.string().trim().max(300);

export const SupportUsedDoc = z.object({ path: z.string(), title: z.string() });

export const SupportMessageDto = z.object({
  id: Uuid,
  conversationId: Uuid,
  seq: z.number().int(),
  role: z.enum(SUPPORT_MESSAGE_ROLES),
  authorName: z.string().nullable(),
  /** Plain text with `**bold**` and `[label](url)` only. Render it as text; links outside app routes are not links. */
  text: z.string(),
  confidence: z.number().nullable(),
  usedDocs: z.array(SupportUsedDoc),
  outcome: z.enum(SUPPORT_OUTCOMES).nullable(),
  feedback: z.enum(["up", "down"]).nullable(),
  feedbackNote: z.string().nullable(),
  createdAt: IsoDateTime,
});
export type SupportMessageDto = z.infer<typeof SupportMessageDto>;

export const SupportConversationDto = z.object({
  id: Uuid,
  status: z.enum(SUPPORT_CONVERSATION_STATUSES),
  subject: z.string(),
  page: z.string().nullable(),
  language: UiLang,
  createdByName: z.string().nullable(),
  /** Opened by the person asking. */
  mine: z.boolean(),
  assigneeName: z.string().nullable(),
  unread: z.number().int(),
  lastMessageAt: IsoDateTime,
  lastMessagePreview: z.string(),
  lastMessageRole: z.enum(SUPPORT_MESSAGE_ROLES),
  source: z.enum(["chat", "group"]),
  escalationReason: z.enum(SUPPORT_ESCALATION_REASONS).nullable(),
  closedAt: IsoDateTime.nullable(),
  createdAt: IsoDateTime,
});
export type SupportConversationDto = z.infer<typeof SupportConversationDto>;

export const SupportConversationQuery = PageQuery.extend({ cursor: Uuid.optional(), status: z.enum(SUPPORT_CONVERSATION_STATUSES).optional() });
export type SupportConversationQuery = z.infer<typeof SupportConversationQuery>;
export const SupportConversationPage = pageOf(SupportConversationDto);

export const SupportThreadQuery = z.object({
  /** Only what came after this, for polling. */
  afterSeq: z.coerce.number().int().min(0).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type SupportThreadQuery = z.infer<typeof SupportThreadQuery>;

export const SupportThreadResponse = z.object({ conversation: SupportConversationDto, messages: z.array(SupportMessageDto) });
export type SupportThreadResponse = z.infer<typeof SupportThreadResponse>;

/** Who writes is the session's. `clientId` makes a double tap one message. */
export const SupportSendRequest = z.object({
  clientId: ClientId,
  text: SupportText,
  page: Page.optional(),
  language: UiLang.optional(),
});
export type SupportSendRequest = z.infer<typeof SupportSendRequest>;

export const SupportSendResponse = z.object({
  conversation: SupportConversationDto,
  message: SupportMessageDto,
  /** What was written after it: the AI's answer, or the plain message saying a person will reply. */
  replies: z.array(SupportMessageDto),
  deduped: z.boolean(),
});
export type SupportSendResponse = z.infer<typeof SupportSendResponse>;

export const SupportFeedbackRequest = z.object({ rating: z.enum(["up", "down"]), note: z.string().trim().max(1000).optional() });
export type SupportFeedbackRequest = z.infer<typeof SupportFeedbackRequest>;

export const SupportUnreadResponse = z.object({ count: z.number().int() });

/** What the help panel says before anyone writes. Each false has its own plain message on the screen. */
export const SupportStatusResponse = z.object({
  aiAvailable: z.boolean(),
  kbAvailable: z.boolean(),
  limitReached: z.boolean(),
  /** Shown above the composer: on a medical tenant, not to write patient data here. */
  notice: z.string().nullable(),
});
export type SupportStatusResponse = z.infer<typeof SupportStatusResponse>;

// ---------------------------------------------------------------------------
// Reading the documents
// ---------------------------------------------------------------------------

export const SupportDocSearchQuery = z.object({
  q: z.string().trim().min(2).max(500),
  language: UiLang.optional(),
  limit: z.coerce.number().int().min(1).max(8).default(8),
});
export type SupportDocSearchQuery = z.infer<typeof SupportDocSearchQuery>;

export const SupportDocHit = z.object({
  path: z.string(),
  title: z.string(),
  category: z.string(),
  subcategory: z.string(),
  area: z.string(),
  language: z.string(),
  score: z.number(),
  excerpt: z.string(),
});
export type SupportDocHit = z.infer<typeof SupportDocHit>;
export const SupportDocSearchResponse = z.object({ items: z.array(SupportDocHit) });

export const SupportDocReadQuery = z.object({ path: z.string().trim().min(1).max(300) });
export type SupportDocReadQuery = z.infer<typeof SupportDocReadQuery>;

export const SupportDocView = z.object({
  path: z.string(),
  title: z.string(),
  category: z.string(),
  subcategory: z.string(),
  area: z.string(),
  language: z.string(),
  tags: z.array(z.string()),
  body: z.string(),
});
export type SupportDocView = z.infer<typeof SupportDocView>;

/** `support_ask` over MCP: the answer alone, with no thread behind it. */
export const SupportAskResult = z.object({
  outcome: z.enum(SUPPORT_OUTCOMES),
  text: z.string(),
  usedDocs: z.array(SupportUsedDoc),
  links: z.array(z.string()),
  confidence: z.number().nullable(),
  /** Why no answer was attempted or kept. Null when the model answered. */
  reason: z.enum(SUPPORT_ESCALATION_REASONS).nullable(),
});
export type SupportAskResult = z.infer<typeof SupportAskResult>;

// ---------------------------------------------------------------------------
// The console
// ---------------------------------------------------------------------------

const QueryBoolean = z.enum(["true", "false"]).transform((value) => value === "true");

export const SupportInboxQuery = PageQuery.extend({
  cursor: Uuid.optional(),
  status: z.enum(SUPPORT_CONVERSATION_STATUSES).optional(),
  hostId: Uuid.optional(),
  tenantId: Uuid.optional(),
  /** Assigned to whoever asks. */
  mine: QueryBoolean.optional(),
});
export type SupportInboxQuery = z.infer<typeof SupportInboxQuery>;

export const SupportInboxItem = SupportConversationDto.omit({ mine: true, unread: true }).extend({
  tenantId: Uuid,
  tenantName: z.string(),
  tenantVertical: z.string(),
  hostId: Uuid,
  hostSlug: z.string(),
  assigneeId: z.string().nullable(),
  unreadStaff: z.number().int(),
});
export type SupportInboxItem = z.infer<typeof SupportInboxItem>;
export const SupportInboxPage = pageOf(SupportInboxItem);

export const SupportInboxCounts = z.object({ waiting: z.number().int(), human: z.number().int(), unread: z.number().int(), mine: z.number().int() });
export type SupportInboxCounts = z.infer<typeof SupportInboxCounts>;

export const SupportConsoleThread = z.object({ conversation: SupportInboxItem, messages: z.array(SupportMessageDto) });
export type SupportConsoleThread = z.infer<typeof SupportConsoleThread>;

export const SupportReplyRequest = z.object({ clientId: ClientId, text: SupportText });
export type SupportReplyRequest = z.infer<typeof SupportReplyRequest>;

export const SupportQuickReplyDto = z.object({
  id: Uuid,
  hostId: Uuid.nullable(),
  language: UiLang,
  title: z.string(),
  text: z.string(),
  sortOrder: z.number().int(),
});
export type SupportQuickReplyDto = z.infer<typeof SupportQuickReplyDto>;
export const SupportQuickReplyList = z.object({ items: z.array(SupportQuickReplyDto) });
export const SupportQuickReplyQuery = z.object({ language: UiLang.optional(), hostId: Uuid.optional() });
export type SupportQuickReplyQuery = z.infer<typeof SupportQuickReplyQuery>;
export const SupportQuickReplyCreate = z.object({
  /** Platform operators only: which host it belongs to, none for all of them. A host operator's is always its own. */
  hostId: Uuid.nullable().optional(),
  language: UiLang,
  title: z.string().trim().min(1).max(120),
  text: SupportText,
  sortOrder: z.number().int().min(0).max(10_000).default(0),
});
export type SupportQuickReplyCreate = z.infer<typeof SupportQuickReplyCreate>;
export const SupportQuickReplyPatch = SupportQuickReplyCreate.omit({ hostId: true }).partial();
export type SupportQuickReplyPatch = z.infer<typeof SupportQuickReplyPatch>;

export const SupportGroupMessageRequest = z.object({
  /** One send, however many times it is retried. */
  clientId: ClientId,
  tenantIds: z.array(Uuid).min(1).max(500),
  subject: z.string().trim().min(1).max(160),
  text: SupportText,
});
export type SupportGroupMessageRequest = z.infer<typeof SupportGroupMessageRequest>;
export const SupportGroupMessageResponse = z.object({
  groupId: Uuid,
  results: z.array(z.object({ tenantId: Uuid, conversationId: Uuid.nullable(), outcome: z.enum(["sent", "duplicate", "not_reachable", "failed"]) })),
});
export type SupportGroupMessageResponse = z.infer<typeof SupportGroupMessageResponse>;

export const SupportLimitPut = z.object({ dailyLimit: z.number().int().min(0).max(100_000).nullable() });
export type SupportLimitPut = z.infer<typeof SupportLimitPut>;
export const SupportLimitResponse = z.object({ tenantId: Uuid, dailyLimit: z.number().int(), custom: z.boolean(), usedToday: z.number().int() });
export type SupportLimitResponse = z.infer<typeof SupportLimitResponse>;

const DocumentMeta = {
  title: z.string().trim().min(3).max(200),
  category: z.string().trim().min(1).max(80),
  subcategory: z.string().trim().min(1).max(80),
  tags: z.array(z.string().trim().min(1).max(60)).max(30),
  priority: z.number().int().min(1).max(10).default(5),
  verticals: z.array(z.enum(SUPPORT_VERTICALS)).default([]),
  body: z.string().trim().min(1).max(12_000),
};

export const SupportDocumentSummary = z.object({
  id: Uuid,
  hostId: Uuid.nullable(),
  language: z.string(),
  path: z.string(),
  area: z.string(),
  title: z.string(),
  category: z.string(),
  subcategory: z.string(),
  tags: z.array(z.string()),
  priority: z.number().int(),
  verticals: z.array(z.string()),
  status: z.enum(SUPPORT_DOCUMENT_STATUSES),
  source: z.enum(SUPPORT_DOCUMENT_SOURCES),
  hasVector: z.boolean(),
  updatedAt: IsoDateTime,
});
export type SupportDocumentSummary = z.infer<typeof SupportDocumentSummary>;
export const SupportDocumentPage = pageOf(SupportDocumentSummary);
export const SupportDocumentDto = SupportDocumentSummary.extend({ body: z.string(), readOnly: z.boolean() });
export type SupportDocumentDto = z.infer<typeof SupportDocumentDto>;

export const SupportDocumentQuery = PageQuery.extend({
  cursor: Uuid.optional(),
  /** `global` for the repo's, or a host's id. Left out: all an operator can see. */
  scope: z.union([z.literal("global"), Uuid]).optional(),
  language: z.string().min(2).max(5).optional(),
  status: z.enum(SUPPORT_DOCUMENT_STATUSES).optional(),
  category: z.string().max(80).optional(),
  q: z.string().trim().max(200).optional(),
});
export type SupportDocumentQuery = z.infer<typeof SupportDocumentQuery>;

export const SupportDocumentCreate = z.object({
  /** Platform operators only: which host's. A host operator's is always its own. */
  hostId: Uuid.optional(),
  language: UiLang,
  area: z.string().trim().regex(/^[a-z0-9-]{1,60}$/),
  slug: z.string().trim().regex(/^[a-z0-9-]{1,80}$/),
  status: z.enum(["active", "draft"]).default("draft"),
  ...DocumentMeta,
});
export type SupportDocumentCreate = z.infer<typeof SupportDocumentCreate>;

export const SupportDocumentPatch = z
  .object({ title: DocumentMeta.title, category: DocumentMeta.category, subcategory: DocumentMeta.subcategory, tags: DocumentMeta.tags, priority: z.number().int().min(1).max(10), verticals: z.array(z.enum(SUPPORT_VERTICALS)), body: DocumentMeta.body, status: z.enum(["active", "draft"]) })
  .partial();
export type SupportDocumentPatch = z.infer<typeof SupportDocumentPatch>;

export const SupportCategoriesResponse = z.object({ items: z.array(z.object({ category: z.string(), subcategory: z.string(), documents: z.number().int() })) });

export const SupportImportStatus = z.object({
  kbVersion: z.number().int(),
  lastImportAt: IsoDateTime.nullable(),
  lastImport: z.object({ created: z.number().int(), updated: z.number().int(), unchanged: z.number().int(), retired: z.number().int(), embedded: z.number().int(), failed: z.array(z.string()) }).nullable(),
  documents: z.object({ active: z.number().int(), draft: z.number().int(), retired: z.number().int(), withoutVector: z.array(z.string()) }),
});
export type SupportImportStatus = z.infer<typeof SupportImportStatus>;

export const SupportProposalDto = z.object({
  id: Uuid,
  hostId: Uuid,
  language: z.string(),
  title: z.string(),
  category: z.string(),
  subcategory: z.string(),
  tags: z.array(z.string()),
  body: z.string(),
  threads: z.number().int(),
  sourceConversationIds: z.array(Uuid),
  status: z.enum(SUPPORT_PROPOSAL_STATUSES),
  reviewedBy: z.string().nullable(),
  reviewedAt: IsoDateTime.nullable(),
  documentId: Uuid.nullable(),
  createdAt: IsoDateTime,
});
export type SupportProposalDto = z.infer<typeof SupportProposalDto>;
export const SupportProposalPage = pageOf(SupportProposalDto);
export const SupportProposalQuery = PageQuery.extend({ cursor: Uuid.optional(), status: z.enum(SUPPORT_PROPOSAL_STATUSES).optional(), hostId: Uuid.optional() });
export type SupportProposalQuery = z.infer<typeof SupportProposalQuery>;

/** What a person changed goes with the yes. Approving makes a draft: it still is not published. */
export const SupportProposalApprove = z.object({
  scope: z.enum(["host", "global"]).default("host"),
  title: DocumentMeta.title.optional(),
  category: DocumentMeta.category.optional(),
  subcategory: DocumentMeta.subcategory.optional(),
  tags: DocumentMeta.tags.optional(),
  body: DocumentMeta.body.optional(),
});
export type SupportProposalApprove = z.infer<typeof SupportProposalApprove>;
export type SupportOutcome = (typeof SUPPORT_OUTCOMES)[number];
export type SupportEscalationReason = (typeof SUPPORT_ESCALATION_REASONS)[number];
export type SupportConversationStatus = (typeof SUPPORT_CONVERSATION_STATUSES)[number];
export type SupportMessageRole = (typeof SUPPORT_MESSAGE_ROLES)[number];
